import React from 'react';
import cx from 'classnames';

import Icon from '../Icon';
import Text from '../Text';

import styles from './Button.module.scss';
import usePresenter from './Button.presenter';
import getProps from './Button.props';
import { ButtonCombinedProps } from './types';
import LoadingSpinner from '../LoadingSpinner';
import { LoadingSpinnerSizeEnum, LoadingSpinnerStyleEnum } from '../LoadingSpinner/types';


// MANUAL OVERRIDE - converted to forwardRef Component
const Button = React.forwardRef<HTMLButtonElement, ButtonCombinedProps>((props, ref) => {
  const {
    type,
    size,
    styleType,
    state,
    className,
    classes,
    disabled,
    buttonType,
    buttonForm,
    onClick,
    icon,
    text,
    tabIndex, // MANUAL OVERRIDE
    dataTestId, // MANUAL OVERRIDE
    onMouseDown, // MANUAL OVERRIDE
    onMouseEnter, // MANUAL OVERRIDE
    onMouseLeave, // MANUAL OVERRIDE
    ...otherProps // MANUAL OVERRIDE
  } = usePresenter(props);

  const variantName = `${type}${size}${styleType}${state}`;
  const internalProps = getProps(variantName);
  const currentStyle = styles[`button${variantName}`];

  const buttonIconView =
  state === 'Loading' ? null : (
    <Icon
      className={cx(styles.icon, classes?.icon)}
      {...internalProps.icon}
      {...icon}
    />
  );

  let loadingSpinnerSize: LoadingSpinnerSizeEnum = 'Small';
  let loadingSpinnerStyleType: LoadingSpinnerStyleEnum = 'Black';

  switch (size) {
    case 'L':
    // falls through
    case 'M':
    // falls through
    case 'S':
      loadingSpinnerSize = 'Small';
      break;
    default:
      loadingSpinnerSize = 'Small';
  }

  switch (styleType) {
    case 'Primary':
    // falls through
    case 'PrimarySubdued':
    // falls through
    case 'PrimaryOutline':
      loadingSpinnerStyleType = 'Primary';
      break;
    default:
      loadingSpinnerStyleType = 'Black';
  }

  const loadingSpinnerView =
    state === 'Loading' ? (
      <LoadingSpinner
        className={cx(styles.loadingSpinner, className)}
        size={loadingSpinnerSize}
        styleType={loadingSpinnerStyleType}
        isActive
      />
    ) : null;

  let componentView;

  switch (variantName) {
    case 'ComponentIconSDestructiveDefault':
    case 'ComponentIconSPositiveDefault':
    case 'IconNoBGSPrimaryDefault':
    case 'IconMPrimarySubduedDefault':
    case 'IconSPrimarySubduedDefault':
    case 'CopyIconSPrimarySubduedDefault':
    case 'FieldIconSDarkDefault':
    case 'FieldIconSDarkSelected':
    case 'FieldIconSPrimaryDefault':
    case 'FieldIconSPrimarySelected':
    case 'FieldIconSPositiveDefault':
    case 'FieldIconSPositiveSelected': {
      componentView = (
        <button
          disabled={disabled}
          type={buttonType}
          form={buttonForm}
          onClick={onClick}
          onMouseDown={onMouseDown} // MANUAL OVERRIDE
          onMouseEnter={onMouseEnter} // MANUAL OVERRIDE
          onMouseLeave={onMouseLeave} // MANUAL OVERRIDE
          tabIndex={tabIndex} // MANUAL OVERRIDE
          data-testid={dataTestId} // MANUAL OVERRIDE
          className={cx(currentStyle, className)}
          ref={ref} // MANUAL OVERRIDE
          {...otherProps} // MANUAL OVERRIDE
        >
          <Icon
            className={cx(styles.icon, classes?.icon)}
            {...internalProps.icon}
            {...icon}
          />
        </button>
      );
      break;
    }
    case 'IconTextMPrimarySubduedDefault':
    case 'IconTextNoBGSPrimaryLightDefault':
    case 'IconTextNoBGSPrimaryDefault':
    case 'IconTextMPrimaryOutlineLoaded':
    case 'IconTextMPrimaryOutlineLoading':
    case 'IconTextMPrimarySubduedLoading':
    case 'IconTextMPrimarySubduedLoaded': {
      componentView = (
        <button
          disabled={disabled}
          type={buttonType}
          form={buttonForm}
          onClick={onClick}
          onMouseDown={onMouseDown} // MANUAL OVERRIDE
          onMouseEnter={onMouseEnter} // MANUAL OVERRIDE
          onMouseLeave={onMouseLeave} // MANUAL OVERRIDE
          tabIndex={tabIndex} // MANUAL OVERRIDE
          data-testid={dataTestId} // MANUAL OVERRIDE
          className={cx(currentStyle, className)}
          ref={ref} // MANUAL OVERRIDE
          {...otherProps} // MANUAL OVERRIDE
        >
          {buttonIconView}
          {loadingSpinnerView}
          <Text
            className={cx(styles.text, classes?.text)}
            {...internalProps.text}
            {...text}
          />
        </button>
      );
      break;
    }
    case 'TextIconMPrimaryDefault':
    case 'TextIconSPrimaryDefault':
    case 'TextIconSPrimarySubduedDefault':
    case 'TextLPrimaryLoaded':
    case 'TextLPrimaryLoading':
    case 'TextMPrimaryLoaded':
    case 'TextMPrimaryLoading':
    case 'TextSPrimaryLoaded':
    case 'TextSPrimaryLoading':
    case 'TextSPrimarySubduedLoaded':
    case 'TextSPrimarySubduedLoading': {
      componentView = (
        <button
          disabled={disabled}
          type={buttonType}
          form={buttonForm}
          onClick={onClick}
          onMouseDown={onMouseDown} // MANUAL OVERRIDE
          onMouseEnter={onMouseEnter} // MANUAL OVERRIDE
          onMouseLeave={onMouseLeave} // MANUAL OVERRIDE
          tabIndex={tabIndex} // MANUAL OVERRIDE
          data-testid={dataTestId} // MANUAL OVERRIDE
          className={cx(currentStyle, className)}
          ref={ref} // MANUAL OVERRIDE
          {...otherProps} // MANUAL OVERRIDE
        >
          <Text
            className={cx(styles.text, classes?.text)}
            {...internalProps.text}
            {...text}
          />
          {buttonIconView}
          {loadingSpinnerView}
        </button>
      );
      break;
    }
    case 'TextLDestructiveDefault':
    case 'TextLPrimaryDefault':
    case 'TextLPrimarySubduedDefault':
    case 'TextMDestructiveDefault':
    case 'TextMDestructiveSubduedDefault':
    case 'TextMPrimaryDefault':
    case 'TextMPrimarySubduedDefault':
    case 'TextSPrimaryDefault':
    case 'TextSPrimarySubduedDefault':
    case 'TextNoBGSPrimaryDefault':
    case 'TextUnderlineSPrimaryDefault': {
      componentView = (
        <button
          disabled={disabled}
          type={buttonType}
          form={buttonForm}
          onClick={onClick}
          onMouseDown={onMouseDown} // MANUAL OVERRIDE
          onMouseEnter={onMouseEnter} // MANUAL OVERRIDE
          onMouseLeave={onMouseLeave} // MANUAL OVERRIDE
          tabIndex={tabIndex} // MANUAL OVERRIDE
          data-testid={dataTestId} // MANUAL OVERRIDE
          className={cx(currentStyle, className)}
          ref={ref} // MANUAL OVERRIDE
          {...otherProps} // MANUAL OVERRIDE
        >
          <Text
            className={cx(styles.text, classes?.text)}
            {...internalProps.text}
            {...text}
          />
        </button>
      );
      break;
    }
    default:
      componentView = <div>�</div>;
      break;
  }

  return componentView;
}); // MANUAL OVERRIDE

export default Button;
